import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  SortDirection,
} from "@mui/material";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useHistory } from "react-router";
import { TbDownload, TbFileInvoice, TbPlus } from "react-icons/tb";
import { ScreenGreeting } from "../../../Components/Molecules";
import { useFilter } from "../../../Hooks/useFilter";
import { useAuth } from "../../../Contexts/AuthContext";
import api from "../../../Services/api";
import { ModalLoader } from "../../../Components/Atoms/ModalLoader";
import moment from "moment";
import { formatCurrency, autoFormatDocument } from "../../../Utils/formatters";
import { returningObjectWithoutNullProperties } from "../../../Utils/checkObjectProperties";
import { TicketsFilterBar } from "./TicketsFilterBar";
import {
  applyTicketsClientFilters,
  filtersFromTicketsFilterObj,
  ticketsFilterObjFromFilters,
} from "./ticketsFilters";

const statusBadgeClass: Record<string, string> = {
  opened: "bg-gray-100 text-gray-600",
  Aguardando: "bg-gray-100 text-gray-600",
  paid: "bg-emerald-50 text-emerald-700",
  Pago: "bg-emerald-50 text-emerald-700",
  overdue_paid: "bg-emerald-50 text-emerald-700",
  canceled: "bg-red-50 text-red-600",
  Cancelado: "bg-red-50 text-red-600",
  overdue: "bg-amber-50 text-amber-700",
  expired: "bg-amber-50 text-amber-700",
  Vencido: "bg-amber-50 text-amber-700",
};

const toCurrency = Intl.NumberFormat("pt-BR", {
  currency: "BRL",
  style: "currency",
}).format;

const StatusBadge = ({ label, status }: { label: string; status: string }) => (
  <span
    className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
      statusBadgeClass[status] || "bg-gray-100 text-gray-600"
    }`}
  >
    {label}
  </span>
);

export const Tickets = () => {
  const [table, setTable] = useState({
    totalRows: 20,
    page: 1,
    page_size: 50,
    items: [] as any[],
  });

  const [selectedIds, setSelectedIds] = useState(new Set<string>([]));
  const [dashboard, setDashboard] = useState<any[]>([]);
  const [requestsCount, setRequestsCount] = useState(0);
  const [filters, setFilters] = useState(null);

  const [orderBy, setOrderBy] = useState<Record<string, SortDirection>>({
    CreateAt: "desc",
  });

  const handleOrderBy = (key: string) => {
    setOrderBy((oldOrderBy) => {
      const direction = oldOrderBy[key];
      return {
        [key]: direction === "asc" ? "desc" : "asc",
      };
    });
  };

  const history = useHistory();
  const { hasPermission } = useAuth();
  const { getFilterObj, handleRemoveFilters } = useFilter();
  const ticketsFilterObj = getFilterObj("tickets") as Record<string, any>;
  const apiFilters = useMemo(
    () => returningObjectWithoutNullProperties({ ...ticketsFilterObj }),
    [ticketsFilterObj],
  );

  useEffect(() => {
    setFilters(filtersFromTicketsFilterObj(ticketsFilterObj));
  }, [
    ticketsFilterObj.start_date,
    ticketsFilterObj.end_date,
    ticketsFilterObj.due_date_start,
    ticketsFilterObj.due_date_end,
    ticketsFilterObj.start_amount,
    ticketsFilterObj.end_amount,
    ticketsFilterObj.document,
    ticketsFilterObj.name,
    ticketsFilterObj.type,
    ticketsFilterObj.isAnticipation,
  ]);

  const getBoletos = async (params = {}) => {
    try {
      setRequestsCount((prev) => prev + 1);

      const response = await api.get("boleto/paged", {
        params: { ...params },
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
      });

      setTable({
        items: response.data.items,
        page: response.data.currentPage,
        page_size: response.data.pageSize,
        totalRows: response.data.rowCount,
      });
    } finally {
      setRequestsCount((prev) => prev - 1);
    }
  };

  const getBoletoDashboard = async (params = {}) => {
    try {
      setRequestsCount((prev) => prev + 1);
      const response = await api.get("boleto/dashboard", { params: { ...params } });
      setDashboard(response.data);
    } finally {
      setRequestsCount((prev) => prev - 1);
    }
  };

  const load = async ({
    page = undefined,
    page_size = undefined,
    dashboard: loadDashboard = false,
  }: {
    page?: number;
    page_size?: number;
    dashboard?: boolean;
  } = {}) => {
    const orderByQuery = Object.entries(orderBy).map(([key, dir]) => `${key} ${dir}`);

    await getBoletos({
      page: page || table.page,
      page_size: page_size || table.page_size,
      orderBy: orderByQuery,
      ...apiFilters,
    });

    if (loadDashboard) await getBoletoDashboard({ ...apiFilters });
  };

  const batchDownload = async () => {
    try {
      setRequestsCount((prev) => prev + 1);

      const response = await api.get("Boleto/print/bulk", {
        params: {
          ...apiFilters,
          ids: Array.from(selectedIds),
        },
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/zip" });
      const downloadUrl = URL.createObjectURL(blob);
      const $a = document.createElement("a");
      $a.href = downloadUrl;
      $a.download = "comprovantes.zip";
      document.body.appendChild($a);
      $a.click();
      $a.remove();
      URL.revokeObjectURL(downloadUrl);
    } finally {
      setRequestsCount((prev) => prev - 1);
    }
  };

  useEffect(() => {
    load({ page: 1, dashboard: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsFilterObj, orderBy]);

  const displayItems = useMemo(
    () => applyTicketsClientFilters(table.items, filters ?? {}),
    [table.items, filters],
  );

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setSelectedIds(new Set([]));
    handleRemoveFilters("tickets", ticketsFilterObjFromFilters(nextFilters));
  };

  const displayHeader = useMemo(() => {
    if (selectedIds.size === 0) return intl.get("tickets.selectForBulk");
    if (selectedIds.size === 1) return intl.get("tickets.selectedOne");
    return intl.get("tickets.selectedMany", { count: selectedIds.size });
  }, [selectedIds]);

  const isSelectedAll = useMemo(() => {
    if (!displayItems.length) return false;
    for (const row of displayItems) {
      if (!selectedIds.has(row.id)) return false;
    }
    return true;
  }, [displayItems, selectedIds]);

  return (
    <div className="w-full">
      <ModalLoader open={requestsCount > 0} text="Carregando informações" />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <ScreenGreeting
          title={intl.get("phrases.myTickets")}
          subTitle={intl.get("phrases.emittedTicketDetails")}
        />
        {hasPermission("payboleto") && (
          <button
            type="button"
            className="btn-primary shrink-0"
            onClick={() => history.push("/boletos/add")}
          >
            <TbPlus className="h-4 w-4" />
            {intl.get("phrases.newTicket")}
          </button>
        )}
      </div>

      {filters && (
        <div className="mb-4">
          <TicketsFilterBar
            filters={filters}
            onFiltersChange={handleFiltersChange}
            filteredItems={displayItems}
          />
        </div>
      )}

      {dashboard.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {dashboard.map((item) => (
            <div
              key={item.status}
              className="min-h-[120px] rounded-2xl border border-gray-200 bg-white p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {intl.get("tickets.status")}
                </span>
                <StatusBadge label={item.statusDesc} status={item.status} />
              </div>
              <div className="mt-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {intl.get("tickets.quantity")}
                </p>
                <p className="text-2xl font-bold text-brand-dark">
                  {item.total.toLocaleString("pt-BR")}
                </p>
              </div>
              <div className="mt-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {intl.get("tickets.totalAmount")}
                </p>
                <p className="text-lg font-bold text-brand-dark">{toCurrency(item.amount)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600">{displayHeader}</p>
          <button
            type="button"
            className="btn-outline !h-8 !min-h-8 !px-3"
            disabled={selectedIds.size === 0}
            onClick={batchDownload}
          >
            <TbDownload className="h-4 w-4" />
            {intl.get("tickets.downloadSelected")}
          </button>
        </div>

        <TableContainer sx={{ whiteSpace: "nowrap", overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell align="center" sx={{ padding: "8px" }}>
                  <Checkbox
                    checked={isSelectedAll}
                    indeterminate={selectedIds.size > 0 && !isSelectedAll}
                    onChange={() => {
                      if (!isSelectedAll) {
                        setSelectedIds(new Set(displayItems.map((e) => e.id)));
                      } else {
                        setSelectedIds(new Set([]));
                      }
                    }}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center" sx={{ padding: "8px", fontWeight: 600 }}>
                  <TableSortLabel
                    direction={orderBy.PayerName || "asc"}
                    onClick={() => handleOrderBy("PayerName")}
                    active={orderBy.PayerName !== undefined}
                  >
                    {intl.get("tickets.payer")}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ padding: "8px", fontWeight: 600 }}>
                  <TableSortLabel
                    direction={orderBy.CreateAt || "asc"}
                    onClick={() => handleOrderBy("CreateAt")}
                    active={orderBy.CreateAt !== undefined}
                  >
                    {intl.get("tickets.emission")}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ padding: "8px", fontWeight: 600 }}>
                  {intl.get("tickets.dueDate")}
                </TableCell>
                <TableCell align="center" sx={{ padding: "8px", fontWeight: 600 }}>
                  <TableSortLabel
                    direction={orderBy.Amount || "asc"}
                    onClick={() => handleOrderBy("Amount")}
                    active={orderBy.Amount !== undefined}
                  >
                    {intl.get("tickets.amount")}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ padding: "8px", fontWeight: 600 }}>
                  <TableSortLabel
                    direction={orderBy.PaymentAt || "asc"}
                    onClick={() => handleOrderBy("PaymentAt")}
                    active={orderBy.PaymentAt !== undefined}
                  >
                    {intl.get("tickets.payment")}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ padding: "8px", fontWeight: 600 }}>
                  <TableSortLabel
                    direction={orderBy.AmountPaid || "asc"}
                    onClick={() => handleOrderBy("AmountPaid")}
                    active={orderBy.AmountPaid !== undefined}
                  >
                    {intl.get("tickets.amountPaid")}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ padding: "8px", fontWeight: 600 }}>
                  {intl.get("tickets.anticipated")}
                </TableCell>
                <TableCell align="center" sx={{ padding: "8px", fontWeight: 600 }}>
                  {intl.get("tickets.status")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayItems.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell align="center" sx={{ padding: "8px" }}>
                    <Checkbox
                      value={row.id}
                      checked={selectedIds.has(row.id)}
                      onChange={(_, checked) => {
                        setSelectedIds((prev) => {
                          const newSelectedIds = new Set(prev);
                          if (checked) newSelectedIds.add(row.id);
                          else newSelectedIds.delete(row.id);
                          return newSelectedIds;
                        });
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="left" sx={{ padding: "8px" }}>
                    <p className="text-sm font-medium text-brand-dark">
                      {row?.payer?.name || "—"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {row?.payer?.document ? autoFormatDocument(row.payer.document) : "—"}
                    </p>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ padding: "8px" }}
                    title={moment(row?.created_at).format("DD/MM/YYYY HH:mm")}
                  >
                    <span className="text-sm font-semibold text-brand-dark">
                      {moment(row?.created_at).format("DD/MM/YYYY")}
                    </span>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: "8px" }}>
                    <span className="text-sm font-semibold text-brand-dark">
                      {moment(row?.expiration).format("DD/MM/YYYY")}
                    </span>
                  </TableCell>
                  <TableCell align="right" sx={{ padding: "8px" }}>
                    <span className="text-sm font-semibold text-brand-dark">
                      {formatCurrency(row?.amount)}
                    </span>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: "8px" }}>
                    <span className="text-sm font-semibold text-brand-dark">
                      {row.payment_at ? moment(row.payment_at).format("DD/MM/YYYY") : "—"}
                    </span>
                  </TableCell>
                  <TableCell align="right" sx={{ padding: "8px" }}>
                    <span className="text-sm text-gray-600">
                      {row?.amount_paid == null || row?.amount_paid === 0
                        ? "—"
                        : formatCurrency(row.amount_paid)}
                    </span>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: "8px" }}>
                    <StatusBadge
                      label={row.anticipated ? intl.get("tickets.yes") : intl.get("tickets.no")}
                      status={row.anticipated ? "paid" : "opened"}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ padding: "8px" }}>
                    <StatusBadge label={row?.status} status={row?.status} />
                  </TableCell>
                </TableRow>
              ))}
              {displayItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <div className="flex flex-col items-center gap-2 px-4 py-2 text-center">
                      <TbFileInvoice className="h-10 w-10 text-gray-300" aria-hidden />
                      <p className="text-sm font-medium text-gray-600">
                        {intl.get("tickets.empty")}
                      </p>
                      <p className="text-xs text-gray-400">
                        {intl.get("tickets.emptyHint")}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          onPageChange={(_, page) => load({ page: page + 1 })}
          page={table.page - 1}
          rowsPerPage={table.page_size}
          count={table.totalRows}
          rowsPerPageOptions={[10, 50, 100, 250]}
          onRowsPerPageChange={(e) => load({ page_size: Number(e.target.value) })}
          labelRowsPerPage={intl.get("common.rowsPerPage")}
        />
      </div>
    </div>
  );
};
